import { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useTabVisibility } from './useTabVisibility';
import axios from 'axios';
import { convertToModules, getApiEndpoint } from '@/utils/ERPUtils';
import { CloudCog } from 'lucide-react';

import { logger } from '@/utils/logger';
interface Role {
  name: string;
  description?: string;
  color?:string;
}

interface Module {
  id: string;
  name: string;
  description: string;
  category: string;
  is_active: boolean;
}

interface UserRolesData {
  roles: Role[];
  modules: Module[];
  isSuperAdmin: boolean;
  loading: boolean;
  error: string | null;
  isInitialLoad: boolean;
  clearCache: () => void;
}

const CACHE_KEY = 'userRoles';
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes

// Utility function to clear cache
const clearUserRolesCache = (userId?: string) => {
  if (userId) {
    localStorage.removeItem(`${CACHE_KEY}_${userId}`);
  } else {
    // Clear all userRoles cache entries
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(CACHE_KEY)) {
        localStorage.removeItem(key);
      }
    });
  }
};


export const useUserRoles = (): UserRolesData => {
  const { user } = useAuth();
  const isTabVisible = useTabVisibility();
  const [roles, setRoles] = useState<Role[]>([]);
  const [modules, setModules] = useState<Module[]>([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const lastFetchTime = useRef(0);


  useEffect(() => {
    const fetchUserRolesAndModules = async () => {
      if (!user) {
        // Only set loading to false if this is NOT the initial load
        // This prevents showing "No Access" dialog while auth is still loading
        if (!isInitialLoad) {
          setLoading(false);
        }
        return;
      }

      // Skip fetch if not tab visible and data is fresh
      if (!isTabVisible && Date.now() - lastFetchTime.current < CACHE_EXPIRY) {
        return;
      }

      try {
        // Only set loading to true for initial load
        if (isInitialLoad) {
          setLoading(true);
        }
        setError(null);

        // Fetch user's roles
        const rolesRes = await axios.post(getApiEndpoint("GET_USER_ROLES"),{
          email:user?.email
        })
        if(rolesRes.data?.status === "error") throw Error(rolesRes.data?.message)

        const userRolesList: Role[] = rolesRes.data?.message?.data?.roles?.map(e=>({
          name: e?.name,
          description: e?.description || "",
          color:e?.color || "",
        })) || [];

        setRoles(userRolesList);

        // Check if user is Super Admin
        const superAdmin = userRolesList.some(role => role.name === 'Super Admin');
        setIsSuperAdmin(superAdmin);

        // Get core modules that should be available to all users
        const allowedModulesResponse = await axios.get(getApiEndpoint("GET_ALLOWED_MODULES"))
        if (allowedModulesResponse?.data?.message?.status === "error") throw Error(allowedModulesResponse?.data?.message?.message);

        // Filter modules for ALL of the user's roles (not just the first one)
        const userRoleNames = new Set(userRolesList.map((r: Role) => r.name));
        const allowedModules = convertToModules(allowedModulesResponse.data?.message?.data?.filter((e: any) => userRoleNames.has(e?.role)));

        // If Super Admin, get all modules; otherwise get assigned modules + core modules

        let userModules: Module[] =  allowedModules?.filter(e=> superAdmin || e?.is_active) || [];
        setModules(userModules);

        // Cache the result
        const cacheData = {
          data: {
            roles: userRolesList,
            modules: userModules,
            isSuperAdmin: superAdmin
          },
          timestamp: Date.now()
        };
        localStorage.setItem(`${CACHE_KEY}_${user.id}`, JSON.stringify(cacheData));
        
        fetchedRef.current = true;
        lastFetchTime.current = Date.now();
      } catch (err) {
        logger.error('Error fetching user roles and modules:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch user permissions');
      } finally {
        setLoading(false);
        setIsInitialLoad(false);
      }
    };

    fetchUserRolesAndModules();
  }, [user]);

  const clearCache = () => {
    if (user) {
      clearUserRolesCache(user.id);
      fetchedRef.current = false;
      lastFetchTime.current = 0;
      setIsInitialLoad(true);
      setLoading(true);
      
      // Force immediate refetch
      setTimeout(() => {
      }, 100);
    }
  };

  return {
    roles,
    modules,
    isSuperAdmin,
    loading,
    error,
    isInitialLoad,
    clearCache
  };
};